import React from "react"
import { makeStyles } from "tss-react/mui"
import { Popover, Slider, Switch, Typography, Button } from "@mui/material"
import { VolumeUp as VolumeUpIcon, VolumeOff as VolumeOffIcon } from "@mui/icons-material"

/**
 * Per-user volume popover — the ONE control surface for a remote user's
 * listening chain (services/remoteAudio.js). Summoned from two places, both
 * rendering this same App-level instance: the speaker button on their video
 * tile (anchorEl) and the "Volume…" item in the user context menu
 * (anchorPosition).
 *
 * The 0-400% slider is a PRE-gain into the chain; the native <video> volume
 * slider still applies after it (0-100% attenuator), so 0% here is silence no
 * matter what the native bar says. "Even out volume" toggles the light
 * per-user compressor.
 */
const useStyles = makeStyles()((theme) => ({
  body: {
    width: 260,
    padding: theme.spacing(1.5, 2),
  },
  title: {
    fontSize: "0.85rem",
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    "& > span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  sliderRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 0.5),
  },
  compressorRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5),
  },
  compressorLabel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  compressorTitle: {
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
  },
  compressorSub: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },
  resetBtn: {
    marginTop: theme.spacing(0.5),
    textTransform: "none",
    fontSize: "0.75rem",
  },
}))

const MARKS = [
  { value: 0, label: "0" },
  { value: 100, label: "100%" },
  { value: 200, label: "200" },
  { value: 400, label: "400" },
]

const VolumePopover = ({
  open,
  anchorEl = null,
  anchorPosition = null,
  username = "",
  volume = 100,
  compressorOn = false,
  onVolumeChange,
  onCompressorToggle,
  onClose,
}) => {
  const { classes } = useStyles()

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
      anchorEl={anchorEl || undefined}
      anchorPosition={anchorPosition || undefined}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <div className={classes.body}>
        <Typography component="div" className={classes.title}>
          {volume === 0 ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
          <span>{username}</span>
        </Typography>

        <div className={classes.sliderRow}>
          <Slider
            value={volume}
            min={0}
            max={400}
            step={5}
            marks={MARKS}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
            onChange={(e, v) => onVolumeChange && onVolumeChange(v)}
            aria-label={`Volume for ${username}`}
          />
        </div>

        <div className={classes.compressorRow}>
          <div className={classes.compressorLabel}>
            <span className={classes.compressorTitle}>Even out volume</span>
            <span className={classes.compressorSub}>Softens their loud/quiet swings</span>
          </div>
          <Switch
            checked={compressorOn}
            onChange={() => onCompressorToggle && onCompressorToggle()}
            color="primary"
            size="small"
            slotProps={{ input: { "aria-label": "Even out volume" } }}
          />
        </div>

        {volume !== 100 && (
          <Button
            className={classes.resetBtn}
            size="small"
            onClick={() => onVolumeChange && onVolumeChange(100)}
          >
            Reset to 100%
          </Button>
        )}
      </div>
    </Popover>
  )
}

export default VolumePopover
